import "./CaseColumns.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import format from "date-fns/format";
import { DateTime } from "luxon";
import { ComponentType, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CellProps } from "react-table";

import { Case, Cases, SimplePatient, Slide, User } from "pwapi";

import { openDocument } from "shared/actions/caseDetailActions";
import { QueueEnum } from "shared/reducers/casesReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { getSlideRetentionDate } from "shared/utils/dates";
import {
  procedureEnumToLabel,
  sortSpecimens,
} from "shared/utils/specimenUtils";
import { getUserNameStringWithCredentials } from "shared/utils/userUtils";

import AdminLink from "components/AdminLink";
import { BorderlessButton } from "components/customInputs/buttons/Button";
import PatientSexAge from "components/PatientSexAge";
import { canSignoutCase } from "components/viewer/dialogs/utils";
import ViewerLink from "components/ViewerLink";
import ApplicationState from "store/ApplicationState";

export function shouldDisplayPatient(patient: SimplePatient) {
  return (
    patient &&
    (patient.last_name ||
      patient.first_name !== "Unknown" ||
      patient.sex ||
      patient.date_of_birth !== "1900-01-01")
  );
}

export const getFullNameStr = (
  last?: string,
  first?: string,
  middleInitial?: string
): string =>
  `${[last, first].filter((e) => e).join(", ")}${
    middleInitial ? ` ${middleInitial}` : ""
  }`;

export const caseLinkLabel = (
  caseRow: Cases,
  user?: User,
  canSignOutInViewer?: boolean
) => {
  return (
    (user && user.id === caseRow.active_assignment?.id
      ? caseRow.is_screening && caseRow.finalize_assignment_id
        ? "Screen"
        : canSignoutCase(caseRow, user, canSignOutInViewer)
        ? caseRow.is_request_screen_complete
          ? "Screened"
          : undefined
        : undefined
      : undefined) || "View"
  );
};

const CaseColumns =
  <P extends object>(Component: ComponentType<P>) =>
  (props: any) => {
    const features: FeatureFlags = useSelector(
      ({ sys }: ApplicationState) => sys.features
    );
    const user: User | undefined = useSelector(
      ({ auth }: ApplicationState) => auth.user
    );
    const pendingSlideStatuses = [
      Slide.status.PENDING,
      Slide.status.RECEIVED_LAB,
      Slide.status.MANUAL_REVIEW,
      Slide.status.ON_HOLD,
      Slide.status.UPLOADED,
    ];

    const dispatch = useDispatch();

    const columns = useMemo(
      () =>
        [
          ...(props.queue === QueueEnum.All ||
          props.queue === QueueEnum.Screened ||
          props.queue === QueueEnum.Preliminary
            ? [
                {
                  Header: "Status",
                  accessor: "status",
                  disableSortBy: true,
                  minWidth: "6em",
                },
              ]
            : []),
          ...(props.queue !== QueueEnum.Finalized
            ? [
                {
                  Header: "Priority",
                  accessor: "priority",
                  disableSortBy: false,
                  disableSortRemove: true,
                  Cell: ({ row }: CellProps<Cases>) =>
                    row.original.priority === "normal" ? (
                      row.original.patient?.clinic?.tat_priority ? (
                        <strong
                          className={classNames(
                            "Badge",
                            "Badge__priority",
                            "Badge--tat"
                          )}
                        >
                          Priority
                        </strong>
                      ) : null
                    ) : (
                      <strong
                        className={classNames("Badge", "Badge__priority", {
                          "Badge--stat": row.original.priority === "stat",
                          "Badge--rush": row.original.priority === "rush",
                        })}
                      >
                        {row.original.priority}
                      </strong>
                    ),
                },
              ]
            : []),
          {
            Header: "Accession",
            accessor: "accession",
            Cell: ({ row }: CellProps<Cases>) => (
              <>
                <strong className="column__longName" title="Accession">
                  {row.original.accession}
                </strong>
                {row.original.accession_completed && (
                  <>
                    <span title="Accession Completed At">
                      {format(
                        new Date(row.original.accession_completed),
                        "MM/dd/yyyy"
                      )}
                    </span>
                  </>
                )}
                {features?.displayCaseTags && (
                  <div className="UnderConstruction">
                    <small className="Tag">
                      <strong>Amended</strong>
                    </small>
                  </div>
                )}
              </>
            ),
          },
          {
            Header: "Date Collected",
            accessor: "collected_date",
            Cell: ({ row }: any) => {
              const collectedDateTime =
                row.original.collected_date &&
                DateTime.fromISO(row.original.collected_date);

              return (
                <>
                  <span title="Date Collected">
                    {collectedDateTime?.toLocaleString(
                      DateTime.DATETIME_MED_WITH_WEEKDAY
                    )}
                  </span>
                </>
              );
            },
          },
          {
            Header: "Date Received",
            accessor: "received_date",
            //TODO: Check the typescripting on the diff object after replacing any
            Cell: ({ row }: any) => {
              const receivedDateTime =
                row.original.received_date &&
                DateTime.fromISO(row.original.received_date);
              const diff =
                receivedDateTime &&
                DateTime.now().diff(receivedDateTime, ["days", "hours"]);

              return (
                <>
                  <span title="Date Received">
                    {receivedDateTime?.toLocaleString(
                      DateTime.DATETIME_MED_WITH_WEEKDAY
                    )}
                  </span>
                  {(diff?.hours > 0 || diff?.days > 0) &&
                    (props.queue === QueueEnum.Working ||
                      props.queue === QueueEnum.Active) && (
                      <>
                        <strong
                          className={classNames(
                            diff.days && {
                              Badge: diff.hours > 0 || diff.days > 0,
                              Badge__days: diff.hours > 0 || diff.days > 0,
                              "Badge--rush": diff.days >= 1 && diff.days < 2,
                              "Badge--stat": diff.days >= 2,
                            }
                          )}
                        >
                          {diff.days > 0 ? (
                            `${diff.days} Day${
                              diff.days > 1 || diff.days < 1 ? "s" : ""
                            } Ago`
                          ) : diff.hours > 0 ? (
                            `${Math.floor(diff.hours)} Hour${
                              diff.hours > 2 || diff.hours < 1 ? "s" : ""
                            } Ago`
                          ) : (
                            <></>
                          )}
                        </strong>
                      </>
                    )}
                </>
              );
            },
          },

          ...(props.queue === QueueEnum.Finalized
            ? [
                {
                  Header: "Date Finalized",
                  accessor: "finalized_at",
                  Cell: ({ row }: any) => {
                    const finalizedDateTime =
                      row.original.finalized_at &&
                      DateTime.fromISO(row.original.finalized_at);

                    return (
                      <>
                        <span title="Date Collected">
                          {finalizedDateTime?.toLocaleString(
                            DateTime.DATETIME_MED_WITH_WEEKDAY
                          )}
                        </span>
                      </>
                    );
                  },
                },
              ]
            : []),

          {
            Header: "Clinician",
            accessor: "ordering_doctor",
            // Reenable this when the backend properly supports this as a sort
            disableSortBy: true,
            Cell: ({ row }: CellProps<Cases>) => (
              <>
                <span>
                  {row.original.ordering_doctor &&
                    getUserNameStringWithCredentials(
                      row.original.ordering_doctor,
                      true
                    )}
                </span>
                <small title="Clinic">
                  {row.original.patient?.clinic?.name || ""}
                </small>
              </>
            ),
          },
          {
            Header: "Patient",
            accessor: "patient",
            Cell: ({ row }: CellProps<Cases>) => {
              return shouldDisplayPatient(row.original.patient) ? (
                <>
                  <span className="column__longName" title="Patient Name">
                    {getFullNameStr(
                      row.original.patient.last_name || undefined,
                      row.original.patient.first_name
                    )}
                  </span>
                  <small>
                    <PatientSexAge patient={row.original.patient} />
                  </small>
                </>
              ) : (
                <></>
              );
            },
          },
          ...(features?.slideRetentionColumn &&
          props.queue === QueueEnum.Finalized
            ? [
                {
                  Header: "Slide Retention",
                  accessor: "high_res_removal_date",
                  Cell: ({ row }: CellProps<Cases>) =>
                    row.original.high_res_removal_date ? (
                      <small>
                        {getSlideRetentionDate(
                          DateTime.fromISO(row.original.high_res_removal_date)
                        )}
                      </small>
                    ) : (
                      <></>
                    ),
                },
              ]
            : []),
          ...(!features?.hideSlidesCount
            ? [
                {
                  Header: "Slides",
                  accessor: "slides_count",
                  disableSortBy: true,
                  //TODO: Openapi likely isn't describing the 'slide.status' correctly here
                  Cell: ({ row }: CellProps<Cases>) => {
                    if (row.original.specimens) {
                      const specimens = sortSpecimens(row.original.specimens);
                      return (
                        <>
                          {specimens.map((s) => {
                            const successSlidesCount =
                              row.original.slides?.filter(
                                (slide) =>
                                  slide.specimen_id === s.id &&
                                  slide.status === Slide.status.SUCCESS
                              ).length;
                            const pendingSlidesCount =
                              features.slideCountEstimates
                                ? row.original.slides?.filter(
                                    (slide) =>
                                      slide.specimen_id === s.id &&
                                      slide.status &&
                                      pendingSlideStatuses.includes(
                                        slide.status
                                      )
                                  ).length
                                : 0;
                            // const ungrossedSpecimen =
                            //   features.slideCountEstimates &&
                            //   s.lis_state &&
                            //   s.lis_state !== Specimen.lis_state.GROSSED &&
                            //   !row.original.blocks.some(
                            //     (block) => block.specimen_id === s.id
                            //   );
                            const uncutBlocksCount =
                              features.slideCountEstimates
                                ? row.original.blocks.filter(
                                    (block) =>
                                      block.specimen_id === s.id &&
                                      !row.original.slides?.some(
                                        (slide) =>
                                          slide.block_obj_id === block.id
                                      )
                                  ).length
                                : 0;

                            const expectedSlidesCount = uncutBlocksCount;
                            // (ungrossedSpecimen ? 1 : 0) + uncutBlocksCount;

                            return (
                              <span className="slideCount__line" key={s.id}>
                                {s.name}
                                <> &mdash; {successSlidesCount}</>
                                {row.original.source === Case.source.PW &&
                                  typeof pendingSlidesCount !== "undefined" &&
                                  Number.isInteger(pendingSlidesCount) &&
                                  expectedSlidesCount + pendingSlidesCount >
                                    0 &&
                                  typeof successSlidesCount !== "undefined" &&
                                  Number.isInteger(successSlidesCount) && (
                                    <>
                                      {" "}
                                      /{" "}
                                      {successSlidesCount +
                                        expectedSlidesCount +
                                        pendingSlidesCount}
                                      {expectedSlidesCount > 0 ? "+" : ""}
                                    </>
                                  )}
                              </span>
                            );
                          })}
                        </>
                      );
                    } else {
                      return (
                        <>
                          {row.original.slides?.filter(
                            (slide) => slide.status === "success"
                          )?.length || 0}
                        </>
                      );
                    }
                  },
                },
              ]
            : []),
          {
            Header: "Procedure",
            accessor: "procedure",
            Cell: ({ row }: CellProps<Cases>) => {
              if (row.original.specimens) {
                const specimens = sortSpecimens(row.original.specimens);
                return (
                  <>
                    {specimens.map((s) => (
                      <div key={s.id}>
                        {row.original.source === Case.source.WPL
                          ? s.biopsy_type
                          : procedureEnumToLabel(s.procedure)}
                      </div>
                    ))}
                  </>
                );
              }
              return <></>;
            },
          },

          // /*
          //   This diagnosisCategory flag should be set for MRMC users only,
          //   but not other users until it can be implemented further.
          //   (we're hardcoding the MRMC results).
          // */
          ...(features?.diagnosisCategoryFilter
            ? [
                {
                  Header: "Diagnosis Category",
                  accessor: "diagnosis_categories",
                },
              ]
            : []),
          ...(props.queue !== QueueEnum.Finalized
            ? [
                {
                  Header: "Assignment",
                  accessor: "active_assignment",
                  Cell: ({ row }: CellProps<Cases>) =>
                    row.original.is_screening ||
                    row.original.is_request_screen_complete ? (
                      <div className="column__verticalContainer">
                        {row.original.is_request_screen_complete && (
                          <div
                            className={classNames(
                              "column__assignment",
                              "column__assignment--secondary"
                            )}
                          >
                            <span>
                              Screen <FontAwesomeIcon icon="check-circle" />
                            </span>
                          </div>
                        )}
                        <div
                          className={classNames(
                            "column__verticalContainer",
                            "column__assignment",
                            "column__assignment--primary"
                          )}
                        >
                          <span>
                            {row.original.is_screening
                              ? "Screening"
                              : "Finalizing"}
                            :
                          </span>
                          <span>
                            {row.original.active_assignment?.first_name}&nbsp;
                            {row.original.active_assignment?.last_name}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span>
                        {row.original.active_assignment?.first_name}&nbsp;
                        {row.original.active_assignment?.last_name}
                      </span>
                    ),
                },
              ]
            : []),
          {
            Header: "Action",
            disableSortBy: true,
            minWidth: "12em",
            sticky: "right",
            //TODO: Similar to above, 'slide_status' is improperly described in openapi
            Cell: ({ row }: CellProps<Cases>) => {
              const finalizedReport =
                (row.original.status === Case.status.FINALIZED ||
                  row.original.status === Case.status.ARCHIVED) &&
                row.original.finalized_report_id;

              const preliminaryReport =
                (row.original.status === Case.status.WORKING ||
                  row.original.status === Case.status.ACTIVE) &&
                !row.original.finalized_report_id &&
                row.original.prelim_report_id;

              const successfulSlides =
                row.original.slides?.filter(
                  (slide) => slide.status === "success"
                ) || [];
              const viewedSlides = successfulSlides.filter(
                (slide) => slide.viewed
              );
              const slideViewState =
                viewedSlides.length &&
                viewedSlides.length === successfulSlides.length
                  ? "all_slides_viewed"
                  : viewedSlides.length > 0
                  ? "new_slides"
                  : "never_viewed";

              return (
                <div className="columnActions__container">
                  <div className="columnActions--horizontal columnActions__actions">
                    <AdminLink
                      route={`/admin/cases/${row.original.id}/`}
                      title="Case Admin"
                    />
                    <ViewerLink
                      caseId={row.original.id}
                      className={classNames(
                        "columnActions__view",
                        `columnActions__view--${slideViewState}`
                      )}
                      label={`${caseLinkLabel(
                        row.original as Cases,
                        user,
                        features?.signOutInViewer
                      )} →`}
                    />
                  </div>
                  {finalizedReport && (
                    <BorderlessButton
                      title="View Report"
                      size="small"
                      onClick={() => {
                        dispatch(
                          openDocument(
                            row.original.id || 0,
                            row.original.finalized_report_id || 0
                          )
                        );
                      }}
                    >
                      <>
                        Report &nbsp;
                        <FontAwesomeIcon
                          icon="file-export"
                          style={{ color: "var(--blue)" }}
                        />
                      </>
                    </BorderlessButton>
                  )}
                  {preliminaryReport && (
                    <BorderlessButton
                      title="View Preliminary Report"
                      size="small"
                      onClick={() => {
                        dispatch(
                          openDocument(
                            row.original.id || 0,
                            row.original.prelim_report_id || 0
                          )
                        );
                      }}
                    >
                      <>
                        Preliminary Report &nbsp;
                        <FontAwesomeIcon
                          icon="file-export"
                          style={{ color: "var(--blue)" }}
                        />
                      </>
                    </BorderlessButton>
                  )}
                </div>
              );
            },
          },
        ] as any,
      [props.queue, user]
    );

    return <Component columns={columns} features={features} {...props} />;
  };

export default CaseColumns;
